/**
 * Universal Builder — additive block registry (Phase 3.1 / P2).
 *
 * PURPOSE
 * A central place to define FUTURE block types (editor component, read-only
 * renderer, palette metadata, default content, optional validation) WITHOUT
 * adding new hard-coded `switch (block.type)` cases to the three legacy
 * rendering paths (VisionDeckEditor, VisionDeckPreview, PublicDeckView).
 *
 * CONTRACT (do not break)
 * - This registry is ADDITIVE. All 22 existing block types continue to be
 *   handled by their existing legacy switches; this registry is consulted ONLY
 *   as the fallback (`default:` case) when a legacy switch does not know a type.
 * - Unknown types that are also not registered must degrade gracefully (the
 *   render helpers return `null`), exactly like before — never throw.
 * - Registered blocks are NOT automatically added to the builder palette, so
 *   nothing new is exposed to users in this phase.
 *
 * A registered block entry:
 *   {
 *     type: string,                       // unique block.type identifier
 *     palette?: { label, icon, description, category },  // metadata only
 *     getDefaultContent?: () => object,   // default `content` for new blocks
 *     Editor?: (props) => JSX | null,     // editing UI (props mirror BlockRenderer)
 *     View?: (props) => JSX | null,       // read-only render (preview + public)
 *     validate?: (content) => true|string // optional validation hook
 *   }
 */

const _registry = new Map();

export function registerBlock(entry) {
  if (!entry || !entry.type) {
    throw new Error('registerBlock requires an object with a `type`');
  }
  _registry.set(entry.type, entry);
}

export function unregisterBlock(type) {
  _registry.delete(type);
}

export function getRegisteredBlock(type) {
  return _registry.get(type) || null;
}

export function isRegistered(type) {
  return _registry.has(type);
}

/** Palette metadata for registered blocks (not surfaced in this phase). */
export function getRegistryPaletteItems() {
  return Array.from(_registry.values())
    .filter((e) => e.palette)
    .map((e) => ({ type: e.type, ...e.palette }));
}

/** Default content for a registered type, or null if not registered. */
export function getRegistryDefaultContent(type) {
  const entry = _registry.get(type);
  return entry && entry.getDefaultContent ? entry.getDefaultContent() : null;
}

/**
 * Read-only render fallback (used by VisionDeckPreview + PublicDeckView
 * `default:` cases). Returns a JSX element, or null when the type is not
 * registered — preserving the existing graceful-degradation behavior.
 */
export function renderRegistryBlockView(block, style, extra = {}) {
  const entry = block && _registry.get(block.type);
  if (!entry || !entry.View) return null;
  const View = entry.View;
  return <View block={block} style={style} {...extra} />;
}

/**
 * Editor render fallback (used by VisionDeckEditor BlockRenderer `default:`).
 * `props` mirrors what BlockRenderer passes (block, isEditing, onUpdate, ...).
 * Returns null when the type is not registered.
 */
export function renderRegistryBlockEditor(props) {
  const block = props && props.block;
  const entry = block && _registry.get(block.type);
  if (!entry || !entry.Editor) return null;
  const Editor = entry.Editor;
  return <Editor {...props} />;
}
